import { useState, ChangeEvent, useEffect } from "react";
import { styled, Grid, TextField } from "@mui/material";
import { getBase64 } from "../../util/Common";
import FileDropZone from '../FileDropZone';
import AddEditDialog from "./AddEditDialog";
import { maxFileSize } from "../../util/Constants";

const TextFieldWithMargin = styled(TextField)((props) => ({
    marginBottom: 20,
}));

interface FileUploadResult {
    content: string;
    fileName: string;
}

interface UploadFileDialogProps {
    isOpen: boolean;
    currentPath: string;
    maxFiles?: number;
    onClose: () => void;
    onSave: (files: FileUploadResult[]) => void;
    error?: Error | undefined;
}

const UploadFileDialog: React.FC<UploadFileDialogProps> = props => {
    const { isOpen, currentPath, maxFiles = 1, onClose, onSave, error } = props;
    const [filesToSubmit, setFilesToSubmit] = useState<File[]>([]);
    const [fileName, setFileName] = useState<string>('');
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const isMultipleFileMode = maxFiles !== 1;

    useEffect(() => {
        if (!isOpen) {
            setToDefaults();
        }
    }, [isOpen]);

    const onFileUploadChange = async (filesToUpload: File[]) => {
        setIsFormDirty(true);
        if (filesToUpload && filesToUpload.length > 0) {
            setFilesToSubmit(filesToUpload);
            if (!isMultipleFileMode) {
                // For single file mode, set the file name for editing
                setFileName(filesToUpload[0].name);
            }
        } else {
            setFilesToSubmit([]);
            setFileName('');
        }
    }

    const handleFileNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setFileName(event.target.value);
    };

    const setToDefaults = () => {
        setSubmitted(false);
        setIsFormDirty(false);
        setFileName('');
        setFilesToSubmit([]);
    }

    const submitForm = async () => {
        if (filesToSubmit.length > 0) {
            setSubmitted(true);
            
            const fileResults: FileUploadResult[] = [];
            
            for (const file of filesToSubmit) {
                let fileToBase64String = "";
                const result = await getBase64(file);
                if (result) {
                    // strip off the mimeType data
                    let resultParts = result.split(',');
                    if (resultParts) {
                        fileToBase64String = resultParts[1] ?? '';
                    }
                }
                
                // For single file mode, use the manually entered fileName
                // For multiple file mode, use the original file name
                const fileNameToUse = isMultipleFileMode ? file.name : fileName;
                
                // Combine current path with file name for full path
                const fullPath = currentPath === '/' ? `/${fileNameToUse}` : `${currentPath}/${fileNameToUse}`;
                
                fileResults.push({
                    content: fileToBase64String,
                    fileName: fullPath
                });
            }
            
            onSave(fileResults);
        }
    }

    const onSaveHandler = () => {
        if (isFormValid()) {
            submitForm();
        }
    };

    // Reset submitted state when error occurs or when error is cleared
    useEffect(() => {
        if (error) {
            setSubmitted(false);
        }
    }, [error]);

    const onError = () => {
        setSubmitted(false);
    }

    const isFormValid = () => {
        if (!isFormDirty || filesToSubmit.length === 0) {
            return false;
        }
        
        if (isMultipleFileMode) {
            // For multiple files, make sure the number of files is within the limit
            return (maxFiles === 0 || (maxFiles > 0 && filesToSubmit.length <= maxFiles));
        } else {
            // For single file mode, check the manually entered file name
            return fileName.length > 0 && fileName.includes('.');
        }
    }

    const fileNameProps = {
        "aria-label": "file name",
        maxLength: 255,
    };

    const saveButtonText = filesToSubmit.length <= 1 
        ? "Upload" 
        : `Upload ${filesToSubmit.length} Files`;

    return (
        <AddEditDialog
            isOpen={isOpen}
            isSubmitted={submitted}
            titlePrefix="File"
            entityName="Upload"
            onClose={onClose}
            onSave={onSaveHandler}
            validate={isFormValid}
            onError={onError}
            error={error}
            maxWidth="md"
            saveButtonText={saveButtonText}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextFieldWithMargin
                        fullWidth
                        disabled={true}
                        value={currentPath}
                        label="Upload Location"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FileDropZone
                        label={isMultipleFileMode ? `Files* ${maxFiles > 0 ? `(Limit: ${maxFiles})` : ''}` : "File*"}
                        maxFileSize={maxFileSize}
                        maxNumberFiles={maxFiles}
                        multipleFileSelection={isMultipleFileMode}
                        dropInstructionText={isMultipleFileMode ? "Drop your files here" : "Drop your file here"}
                        onChange={onFileUploadChange}
                    />
                </Grid>
                {!isMultipleFileMode && (
                    <Grid item xs={12}>
                        <TextFieldWithMargin
                            itemID="dialog-file-name"
                            fullWidth
                            disabled={submitted}
                            value={fileName}
                            label="File Name"
                            inputProps={fileNameProps}
                            onChange={handleFileNameChange}
                            autoComplete="off"
                            data-cy="dialog-file-name"
                            variant="standard"
                            error={isFormDirty && fileName.length > 0 && !fileName.includes('.')}
                            helperText={isFormDirty && fileName.length > 0 && !fileName.includes('.') ? "File name must include extension" : ""}
                        />
                    </Grid>
                )}
            </Grid>
        </AddEditDialog>
    );
}

export default UploadFileDialog;
